import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList, CdkDragHandle } from '@angular/cdk/drag-drop';
import { input, signal, viewChildren, Component, ChangeDetectionStrategy } from '@angular/core';
import {
  type AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { TranslatePipe } from '@core/i18n-legacy';
import {
  InputComponent,
  ButtonComponent,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  HeadingComponent,
} from '@core/user-interface-legacy';

type TFormValue = Partial<{ options: Array<{ value?: string }> }>;
type RawFormValue = { options: Array<{ value: string }> };

@Component({
  selector: 'rt-question-options',
  templateUrl: './sortable-options.component.html',
  styleUrls: ['./sortable-options.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    HeadingComponent,
    CdkDropList,
    CdkDrag,
    IconComponent,
    CdkDragHandle,
    ReactiveFormsModule,
    InputComponent,
    IconButtonComponent,
    GroupComponent,
    ButtonComponent,
    TranslatePipe,
  ],
})
export class SortableOptionsComponent {
  public optionElementsRef = viewChildren<InputComponent>('optionElementRef');

  public defineForm = input.required({
    transform: (v: AbstractControl<TFormValue, RawFormValue>) =>
      <FormGroup<{ options: FormArray<FormGroup<{ value: FormControl<string> }>> }>>v,
  });

  public activeInputIndex = signal<number | null>(null);

  public onReorder(event: CdkDragDrop<any>): void {
    const options = this.getOptionsControl();
    const control = options.controls[event.previousIndex];
    options.removeAt(event.previousIndex);
    options.insert(event.currentIndex, control);
  }

  public addOption(): void {
    const options = this.getOptionsControl();
    options.push(
      new FormGroup({
        value: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      }),
    );
  }

  public removeOption(control: FormGroup<{ value: FormControl<string> }>): void {
    const options = this.getOptionsControl();
    const index = options.controls.indexOf(control);
    options.removeAt(index);
    control.parent!.markAsDirty();
  }

  public setActiveInput(index: number | null): void {
    this.activeInputIndex.set(index);
  }

  public onEnter(index: number): void {
    const options = this.getOptionsControl().value;
    if (index !== options.length - 1) {
      return;
    }

    this.addOption();
    const elementArray = this.optionElementsRef();
    elementArray[index + 1].setFocus();
  }

  private getOptionsControl() {
    return this.defineForm().controls.options;
  }
}
